/**
 * Topbar search (prototype v108 P 57): "Search clients, talent, bookings...". Results come from
 * /api/admin/staging/search; groups show only when the viewed role's sidebar has that page (View As included).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { canOpen, STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch } from '../api';

type Hit = { id: string; name: string; sub?: string | null };
type Results = { clients: Hit[]; talent: Hit[]; bookings: Hit[] };
const GROUPS: { key: keyof Results; label: string; path: string }[] = [
  { key: 'clients', label: 'Clients', path: 'clients' },
  { key: 'talent', label: 'Talent', path: 'talent' },
  { key: 'bookings', label: 'Bookings', path: 'bookings' }
];

export default function GlobalSearch({ role }: { role: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState<Results | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    let live = true;
    const t = setTimeout(() => { stagingFetch(`/api/admin/staging/search?q=${encodeURIComponent(term)}`).then((d) => { if (live) { setFailed(null); setRes(d); setOpen(true); } }).catch((e) => { if (live) { setFailed(e?.message || 'Search failed'); setRes({ clients: [], talent: [], bookings: [] }); setOpen(true); } }); }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const groups = GROUPS.filter((g) => canOpen(role, g.path) && res && res[g.key].length);
  const go = (path: string, id: string) => { setOpen(false); setQ(''); navigate(`${STAGING_ROOT}/${path}/${id}`); };

  return (
    <div ref={box} className="relative w-full max-w-sm">
      <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
      <input type="search" aria-label="Search clients, talent, bookings" placeholder="Search clients, talent, bookings..." value={q}
        onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)} onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        className="h-8 w-full rounded-md border bg-background pl-8 pr-2 text-sm" />
      {open && res && (
        <div role="listbox" aria-label="Search results" className="absolute left-0 right-0 top-9 z-30 max-h-96 overflow-y-auto rounded-md border bg-card p-1 shadow-lg">
          {failed && <p className="px-3 py-2 text-sm text-destructive">Search failed: {failed}</p>}
          {!failed && groups.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">No matches.</p>}
          {groups.map((g) => (
            <div key={g.key} className="py-1">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
              {res[g.key].map((h) => (
                <button key={h.id} type="button" role="option" aria-selected={false} onClick={() => go(g.path, h.id)} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent">
                  <span className="font-medium">{h.name}</span>{h.sub && <span className="block text-xs text-muted-foreground">{h.sub}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
